import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Coins, TrendingUp, Users2, FileText, Save, Gift } from "lucide-react";

export default function AdminNBToken() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);

  const load = async () => {
    try {
      const [c, s, w] = await Promise.all([
        api.get("/admin/nb-token/config"),
        api.get("/admin/nb-token/stats"),
        api.get("/admin/nb-token/wallets"),
      ]);
      setCfg(c.data);
      setStats(s.data);
      setWallets(w.data?.wallets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load NB Token data");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const update = (k, v) => setCfg((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const body = {
        name: cfg.name,
        symbol: cfg.symbol,
        logo_url: cfg.logo_url,
        current_price: Number(cfg.current_price),
        lock_months: Number(cfg.lock_months),
        max_up_pct: Number(cfg.max_up_pct),
        max_down_pct: Number(cfg.max_down_pct),
        buy_enabled: !!cfg.buy_enabled,
        sell_enabled: !!cfg.sell_enabled,
        mining_enabled: !!cfg.mining_enabled,
        min_buy_usdt: Number(cfg.min_buy_usdt),
        max_buy_usdt: Number(cfg.max_buy_usdt),
        allocation_enabled: !!cfg.allocation_enabled,
        allocation_pct_of_deposit: Number(cfg.allocation_pct_of_deposit),
        first_deposit_only: !!cfg.first_deposit_only,
        min_deposit_for_allocation: Number(cfg.min_deposit_for_allocation),
        certificate_enabled: !!cfg.certificate_enabled,
        referrer_bonus_enabled: !!cfg.referrer_bonus_enabled,
        referrer_bonus_pct: Number(cfg.referrer_bonus_pct),
        daily_mining_pct: Number(cfg.daily_mining_pct),
        lock_purchases: !!cfg.lock_purchases,
        live_price_variance_pct: Number(cfg.live_price_variance_pct),
      };
      await api.post("/admin/nb-token/config", body);
      toast.success("NB Token settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg)
    return (
      <AppLayout variant="admin" requireAdmin>
        <div className="text-slate-400">Loading…</div>
      </AppLayout>
    );

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#EAB308]">
            NB Token Ecosystem
          </div>
          <h1 className="font-heading font-bold text-3xl text-white mt-1">
            NB Token Management
          </h1>
        </div>
        <button
          data-testid="admin-nb-grant-open"
          onClick={() => setGrantOpen(true)}
          className="nb-btn-primary text-xs flex items-center gap-2"
        >
          <Gift size={12} /> Grant Tokens
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat label="Current Price" value={`$${Number(stats.current_price).toFixed(4)}`} color="#00E5FF" icon={TrendingUp} />
          <Stat label="Circulating" value={`${Number(stats.circulating).toFixed(2)}`} sub={`Market cap $${Number(stats.market_cap).toFixed(2)}`} color="#EAB308" icon={Coins} />
          <Stat label="Holders" value={String(stats.holders)} sub={`${stats.certificates_issued} certificates`} color="#8AE234" icon={Users2} />
          <Stat label="Locked" value={`${Number(stats.locked).toFixed(2)}`} sub={`Unlocked ${Number(stats.unlocked).toFixed(2)}`} color="#F97316" icon={FileText} />
        </div>
      )}

      {/* Settings */}
      <div className="nb-card p-5 md:p-6">
        <div className="font-heading text-white text-lg mb-4">Token Settings</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name" value={cfg.name} onChange={(v) => update("name", v)} testid="cfg-name" />
          <Field label="Symbol" value={cfg.symbol} onChange={(v) => update("symbol", v)} testid="cfg-symbol" />
          <Field label="Logo URL" value={cfg.logo_url} onChange={(v) => update("logo_url", v)} testid="cfg-logo" />
          <Field label="Current Price (USDT)" type="number" step="0.0001" value={cfg.current_price} onChange={(v) => update("current_price", v)} testid="cfg-price" />
          <Field label="Lock Months" type="number" value={cfg.lock_months} onChange={(v) => update("lock_months", v)} testid="cfg-lock" />
          <Field label="Max Up Move %" type="number" step="0.1" value={cfg.max_up_pct} onChange={(v) => update("max_up_pct", v)} testid="cfg-max-up" />
          <Field label="Max Down Move %" type="number" step="0.1" value={cfg.max_down_pct} onChange={(v) => update("max_down_pct", v)} testid="cfg-max-down" />
        </div>

        <div className="font-heading text-white text-sm mt-6 mb-3">Buy / Sell / Mining</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Toggle label="Buy Enabled" checked={cfg.buy_enabled} onChange={(v) => update("buy_enabled", v)} testid="cfg-buy" />
          <Toggle label="Sell Enabled" checked={cfg.sell_enabled} onChange={(v) => update("sell_enabled", v)} testid="cfg-sell" />
          <Toggle label="Mining Enabled" checked={cfg.mining_enabled} onChange={(v) => update("mining_enabled", v)} testid="cfg-mining" />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <Field label="Min Buy (USDT)" type="number" value={cfg.min_buy_usdt} onChange={(v) => update("min_buy_usdt", v)} testid="cfg-min-buy" />
          <Field label="Max Buy (USDT)" type="number" value={cfg.max_buy_usdt} onChange={(v) => update("max_buy_usdt", v)} testid="cfg-max-buy" />
        </div>

        <div className="font-heading text-white text-sm mt-6 mb-3">First-Deposit Allocation</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Toggle label="Allocation Enabled" checked={cfg.allocation_enabled} onChange={(v) => update("allocation_enabled", v)} testid="cfg-alloc" />
          <Toggle label="Certificate Enabled" checked={cfg.certificate_enabled} onChange={(v) => update("certificate_enabled", v)} testid="cfg-cert" />
          <Field label="% of Deposit → NBT" type="number" step="0.1" value={cfg.allocation_pct_of_deposit} onChange={(v) => update("allocation_pct_of_deposit", v)} testid="cfg-pct" />
          <Field label="Min Deposit for Allocation (USDT)" type="number" value={cfg.min_deposit_for_allocation} onChange={(v) => update("min_deposit_for_allocation", v)} testid="cfg-min-dep" />
          <Toggle label="First Deposit Only" checked={cfg.first_deposit_only} onChange={(v) => update("first_deposit_only", v)} testid="cfg-first-only" />
        </div>

        <div className="font-heading text-white text-sm mt-6 mb-3">Referrer Bonus + Daily Mining</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Toggle label="Referrer NBT Bonus Enabled" checked={cfg.referrer_bonus_enabled} onChange={(v) => update("referrer_bonus_enabled", v)} testid="cfg-ref-bonus" />
          <Field label="Referrer Bonus % (of direct's NBT)" type="number" step="0.1" value={cfg.referrer_bonus_pct} onChange={(v) => update("referrer_bonus_pct", v)} testid="cfg-ref-pct" />
          <Field label="Daily Mining % (of active investment)" type="number" step="0.01" value={cfg.daily_mining_pct} onChange={(v) => update("daily_mining_pct", v)} testid="cfg-mine-pct" />
        </div>

        <div className="font-heading text-white text-sm mt-6 mb-3">Buys Lock Behavior + Live Price</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Toggle label="Lock Purchases (buys → locked position)" checked={cfg.lock_purchases} onChange={(v) => update("lock_purchases", v)} testid="cfg-lock-buys" />
          <Field label="Live Price Variance % (random walk range)" type="number" step="0.1" value={cfg.live_price_variance_pct} onChange={(v) => update("live_price_variance_pct", v)} testid="cfg-variance" />
        </div>

        <button
          data-testid="admin-nb-save"
          onClick={save}
          disabled={saving}
          className="nb-btn-primary mt-6 flex items-center gap-2 text-xs"
        >
          <Save size={12} /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Wallets */}
      <div className="nb-card p-5 mt-6">
        <div className="font-heading text-white text-lg mb-4">
          Top Holders <span className="text-slate-500 text-sm">({wallets.length})</span>
        </div>
        {wallets.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No wallets yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 uppercase tracking-widest">
                <tr className="border-b border-white/5">
                  <th className="text-left py-2">User</th>
                  <th className="text-left">Code</th>
                  <th className="text-right">Balance</th>
                  <th className="text-right">Locked</th>
                  <th className="text-right">Unlocked</th>
                  <th className="text-right">Allocated</th>
                </tr>
              </thead>
              <tbody>
                {wallets.map((w) => (
                  <tr key={w.user_id} data-testid={`nb-wallet-${w.user_id}`} className="border-b border-white/5">
                    <td className="py-2 text-white">
                      {w.user_name || "—"}
                      <div className="text-[10px] text-slate-500">{w.user_email}</div>
                    </td>
                    <td className="font-mono text-nb-cyan">{w.user_code || "—"}</td>
                    <td className="text-right font-mono text-[#EAB308]">
                      {Number(w.balance_total || 0).toFixed(4)}
                    </td>
                    <td className="text-right font-mono text-orange-400">
                      {Number(w.balance_locked || 0).toFixed(4)}
                    </td>
                    <td className="text-right font-mono text-[#8AE234]">
                      {Number(w.balance_unlocked || 0).toFixed(4)}
                    </td>
                    <td className="text-right font-mono text-slate-300">
                      {Number(w.total_allocated || 0).toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {grantOpen && <GrantModal onClose={() => setGrantOpen(false)} onDone={load} />}
      <ExtendLockSection/>
    </AppLayout>
  );
}

function ExtendLockSection() {
  const [userId, setUserId] = useState("");
  const [months, setMonths] = useState("6");
  const [reason, setReason] = useState("Extended lock — long-term value bonus");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/nb-token/extend-lock", {
        user_id: userId.trim(),
        extend_months: Number(months),
        reason,
      });
      toast.success(`Extended lock for ${data.users} user(s) · ${data.affected_tx} tx · ${data.total_nb.toFixed(4)} NBT total`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extend failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="nb-card p-5 mt-6" style={{ borderColor: "rgba(234,179,8,0.35)" }}>
      <div className="font-heading text-white text-lg mb-4">Extend User Lock (positive notification sent to user)</div>
      <div className="grid md:grid-cols-3 gap-4">
        <label className="block"><div className="text-xs text-slate-400 mb-1">User ID</div><input data-testid="ext-uid" value={userId} onChange={(e)=>setUserId(e.target.value)} className="w-full bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"/></label>
        <label className="block"><div className="text-xs text-slate-400 mb-1">Extend by (months)</div><input data-testid="ext-months" type="number" value={months} onChange={(e)=>setMonths(e.target.value)} className="w-full bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"/></label>
        <label className="block"><div className="text-xs text-slate-400 mb-1">Reason (shown to user)</div><input data-testid="ext-reason" value={reason} onChange={(e)=>setReason(e.target.value)} className="w-full bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"/></label>
      </div>
      <button data-testid="ext-submit" onClick={submit} disabled={busy || !userId || !months} className="mt-4 nb-btn-primary text-xs">
        {busy ? "Extending…" : "Extend Lock + Notify"}
      </button>
    </div>
  );
}

function Stat({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="nb-card p-5">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
        <span>{label}</span>
        {Icon && <Icon size={14} style={{ color }} />}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", step }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-nb-cyan/40 outline-none"
      />
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 cursor-pointer">
      <span className="text-sm text-slate-300">{label}</span>
      <input
        data-testid={testid}
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4"
      />
    </label>
  );
}

function GrantModal({ onClose, onDone }) {
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("admin_grant");
  const [lockMonths, setLockMonths] = useState("");
  const [issueCert, setIssueCert] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        user_id: userId.trim(),
        amount_nb: Number(amount),
        reason,
        issue_certificate: issueCert,
      };
      if (lockMonths) body.lock_months = Number(lockMonths);
      const { data } = await api.post("/admin/nb-token/grant", body);
      toast.success(`Granted ${data.amount_nb} NBT ($${data.value_usdt.toFixed(2)})`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="nb-card p-6 w-full max-w-md">
        <div className="font-heading text-white text-lg mb-4">Grant NBT to User</div>
        <div className="space-y-3">
          <Field label="User ID" value={userId} onChange={setUserId} testid="grant-uid" />
          <Field label="Amount (NBT)" type="number" step="0.0001" value={amount} onChange={setAmount} testid="grant-amount" />
          <Field label="Reason" value={reason} onChange={setReason} testid="grant-reason" />
          <Field label="Lock Months (blank = default)" type="number" value={lockMonths} onChange={setLockMonths} testid="grant-lock" />
          <Toggle label="Issue Certificate" checked={issueCert} onChange={setIssueCert} testid="grant-cert" />
        </div>
        <div className="flex gap-2 mt-6">
          <button data-testid="grant-cancel" onClick={onClose} className="flex-1 border border-white/10 rounded-lg py-2 text-sm text-slate-300 hover:bg-white/5">
            Cancel
          </button>
          <button data-testid="grant-submit" onClick={submit} disabled={busy || !userId || !amount} className="flex-1 nb-btn-primary text-sm">
            {busy ? "Granting…" : "Grant"}
          </button>
        </div>
      </div>
    </div>
  );
}
